import React, { FC } from "react";
import { graphql, PageProps, Link } from "gatsby";
import classnames from "classnames";
import { readingTime } from "reading-time-estimator";
import { GatsbyImage } from "gatsby-plugin-image";
import { RichText, RichTextBlock } from "prismic-reactjs";
import { Layout } from "../components/Layout/Layout";
import { PartialPost, PartialPostData } from "../types/types";
import { SEO } from "../components/SEO/SEO";
import { Container } from "../components/Container/Container";
import { CustomPrismicLink } from "../components/CustomPrismicLink/CustomPrismicLink";
import { Tags } from "./../components/Tag/Tag";
import { Callout } from "../components/Callout/Callout";
import { Breadcrumbs, Breadcrumb } from "@nice-digital/nds-breadcrumbs";
import PencilIcon from "../assets/heroicons/pencil.svg";

import styles from "./Post.module.scss";
import { Share } from "../components/Share/Share";

export type PostPageProps = PageProps<
	{
		prismicPost: PartialPost & {
			data: PartialPostData & {
				category: string;
				content: {
					richText: RichTextBlock[];
					text: string;
				};
				seo_title: string;
				seo_description: string;
			};
		};
	},
	{
		uid: string;
	}
>;

const PostPage: FC<PostPageProps> = ({
	data: {
		prismicPost: {
			// first_publication_date,
			// first_publication_date_formatted,
			tags,
			data: {
				title,
				summary,
				image: { alt, fluid },
				seo_title,
				seo_description,
				author,
				content: { richText, text },
			},
		},
	},
}) => {
	const { minutes } = readingTime(text, 250);
	return (
		<Layout>
			<SEO
				title={seo_title || title}
				description={seo_description || summary}
			/>
			<article>
				<header className={styles.header}>
					<Container isNarrow>
						{/* <div className={styles.info}> */}
						<Breadcrumbs className={styles.breadcrumbs}>
							<Breadcrumb elementType={Link} to="/resource-centre">
								Back to Resources
							</Breadcrumb>
							{/* <Breadcrumb>{title}</Breadcrumb> */}
						</Breadcrumbs>
						<dl
							className={styles.metadata}
							aria-label="Author, date and reading time information"
						>
							{tags.length > 0 && (
								<>
									<dt className="visuallyHidden">Article tags</dt>
									<dd className={styles.fullWidthTags}>
										<Tags tags={tags} />
									</dd>
								</>
							)}
						</dl>
						{/* </div> */}
						<h1 className={styles.heading}>{title}</h1>
						{summary && <p className={styles.lead}>{summary}</p>}
						<div className={styles.info}>
							<dl
								className={styles.metadata}
								aria-label="Author, date and reading time information"
							>
								<dt className="visuallyHidden">Author</dt>
								<dd className={styles.byline}>
									<PencilIcon focusable="false" aria-hidden="true" />
									{`By ${author.document.data.name}`}
								</dd>
								<dt className="visuallyHidden">Reading time</dt>
								<dd className={styles.byline}>~ {minutes} min read</dd>
							</dl>
						</div>

						{/* <Share title={title} /> */}
					</Container>
				</header>

				<Container>
					<div className={styles.heroImage}>
						<GatsbyImage image={fluid} alt={alt} className={styles.image} />
					</div>
				</Container>

				<Container isNarrow className={styles.content}>
					<RichText render={richText} serializeHyperlink={CustomPrismicLink} />
				</Container>

				<Container elementType="footer" isNarrow className={styles.footer}>
					<div className={styles.parent}>
						<GatsbyImage
							className={styles.authorImage}
							image={author.document.data.avatar.fluid}
							alt=""
						/>
						<div>
							<Share title={title} className={styles.share} />
							<dl className={styles.metadata} aria-label="Author information">
								<dt className={styles.byline}>Written by</dt>
								<dd className={styles.byline}>{author.document.data.name}</dd>
							</dl>
							<dl className={classnames(styles.metadata, styles.second)}>
								<dt className="visuallyHidden">Author bio</dt>
								<dd>{author.document.data.bio}</dd>
							</dl>
						</div>
					</div>
				</Container>
			</article>
			<Callout />
		</Layout>
	);
};

export default PostPage;

export const pageQuery = graphql`
	query PostQuery($uid: String!) {
		prismicPost(uid: { eq: $uid }) {
			...PartialPost
			data {
				...PartialPostData
				category
				content {
					richText
					text
				}
				seo_title
				seo_description
			}
		}
	}
`;
